import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ZAPICall, ZAPICallClient } from "@z-api/call";
import { CallTokenService } from "../services/call-token.service";

type OneShotStatus = "input" | "calling" | "error";

@Component({
  selector: "app-one-shot",
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <!-- Input para número -->
    <div class="container" *ngIf="status === 'input'">
      <div class="card">
        <h2>Fazer uma Chamada</h2>
        <p class="label">Digite o número para iniciar a chamada:</p>

        <input
          type="tel"
          [(ngModel)]="phoneInput"
          placeholder="ex: 5511999999999"
          class="phone-input"
          (keyup.enter)="startCall()"
        />

        <button
          class="btn-call"
          (click)="startCall()"
          [disabled]="!phoneInput.trim()"
        >
          Ligar
        </button>
      </div>
    </div>

    <!-- Chamada em progresso -->
    <div class="container" *ngIf="status === 'calling'">
      <div class="card">
        <div class="spinner"></div>
        <h2>One-Shot Call</h2>
        <p class="label">Iniciando chamada para {{ number }}...</p>
        <p class="hint">O widget será aberto automaticamente.</p>
      </div>
    </div>

    <!-- Erro -->
    <div class="container" *ngIf="status === 'error'">
      <div class="card card--error">
        <div class="icon-err">✕</div>
        <h2>Erro na chamada</h2>
        <p class="label">{{ errorMessage }}</p>
        <button class="btn-retry" (click)="reset()">Voltar</button>
      </div>
    </div>
  `,
  styles: [
    `
      .container {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: #0d1117;
        padding: 1rem;
      }

      .card {
        background: #161b22;
        border: 1px solid #30363d;
        border-radius: 16px;
        padding: 2.5rem 3rem;
        text-align: center;
        max-width: 400px;
        width: 100%;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      }

      .card--error {
        border-color: #f44336;
      }

      h2 {
        font-size: 1.4rem;
        font-weight: 600;
        color: #e9edef;
        margin-bottom: 0.5rem;
      }

      .label {
        color: #8696a0;
        font-size: 0.95rem;
        margin-top: 0.5rem;
        margin-bottom: 1.5rem;
      }

      .hint {
        color: #8696a0;
        font-size: 0.85rem;
        margin-top: 1rem;
      }

      /* Input de telefone */
      .phone-input {
        width: 100%;
        padding: 0.85rem 1rem;
        background: #0d1117;
        border: 1px solid #30363d;
        border-radius: 8px;
        color: #e9edef;
        font-size: 0.95rem;
        margin-bottom: 1rem;
        font-family: inherit;
        transition: border-color 0.2s;
        box-sizing: border-box;
      }

      .phone-input:focus {
        outline: none;
        border-color: #00a884;
      }

      .phone-input::placeholder {
        color: #6e7681;
      }

      /* Botão de ligar */
      .btn-call {
        width: 100%;
        padding: 0.85rem 1.5rem;
        background: #00a884;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-call:hover:not(:disabled) {
        background: #008f70;
      }

      .btn-call:disabled {
        background: #30363d;
        color: #6e7681;
        cursor: not-allowed;
      }

      /* Botão retry */
      .btn-retry {
        margin-top: 1.25rem;
        padding: 0.65rem 1.5rem;
        background: #00a884;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-size: 0.95rem;
        cursor: pointer;
        transition: background 0.2s;
      }

      .btn-retry:hover {
        background: #008f70;
      }

      /* Spinner */
      .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #30363d;
        border-top-color: #00a884;
        border-radius: 50%;
        animation: spin 0.9s linear infinite;
        margin: 0 auto 1.25rem;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      /* Ícones */
      .icon-err {
        width: 52px;
        height: 52px;
        background: #f44336;
        color: #fff;
        font-size: 1.6rem;
        font-weight: 700;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.25rem;
      }
    `,
  ],
})
export class OneShotComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private callTokenService = inject(CallTokenService);

  status: OneShotStatus = "input";
  number = "";
  phoneInput = "";
  errorMessage = "";
  private idInstancia = "";
  private authToken = "";

  ngOnInit(): void {
    const idInstancia = this.route.snapshot.paramMap.get("idInstancia");
    const authToken = this.route.snapshot.paramMap.get("authToken");
    const number = this.route.snapshot.paramMap.get("number");

    if (!idInstancia || !authToken) {
      this.status = "error";
      this.errorMessage = "Parâmetros inválidos na URL";
      return;
    }

    this.idInstancia = idInstancia;
    this.authToken = authToken;

    // Armazena as credenciais no serviço
    this.callTokenService.setCredentials(idInstancia, authToken);

    // Se o número foi fornecido na URL, inicia automaticamente
    if (number) {
      this.number = number;
      this.phoneInput = number;
      this.startCall();
    } else {
      // Caso contrário, exibe o formulário
      this.status = "input";
    }
  }

  async startCall(): Promise<void> {
    const phone = this.phoneInput.trim();

    if (!phone) {
      this.errorMessage = "Digite um número válido";
      return;
    }

    this.number = phone;
    this.status = "calling";
    await this.startOneShotCall2();
  }

  reset(): void {
    this.status = "input";
    this.phoneInput = "";
    this.errorMessage = "";
  }

  async startOneShotCall(): Promise<void> {
    try {
      const tokenData = await this.callTokenService.getCallToken();

      const handle = ZAPICall.call({
        instanceId: tokenData.instancia,
        getToken: () => Promise.resolve(tokenData.token),
        number: this.number,
        position: "bottom-right",
        theme: { mode: "dark" },
      });

      // Aguarda a finalização
      await handle.done;
      console.log("Chamada one-shot finalizada");
      this.reset();
    } catch (err) {
      this.status = "error";
      this.errorMessage =
        err instanceof Error
          ? err.message
          : "Erro desconhecido ao iniciar chamada";
      console.error("Erro na chamada one-shot:", err);
    }
  }

  async startOneShotCall2(): Promise<void> {
    try {
      const tokenData = await this.callTokenService.getCallToken();

      const client = new ZAPICallClient({
        instanceId: tokenData.instancia,
        getToken: () => Promise.resolve(tokenData.token),
        autoWidget: true,
        minimal: true,
        position: "bottom-right",
        theme: { mode: "dark" },
      });

      let callId: string | null = null;

      client.on("call:outgoing", (call) => {
        callId = call.callId;
        window.parent.postMessage({ callId: callId, phone: call.from, isZapiCall: true }, "*");
        console.log("[one-shot] callId:", callId);
      });

      client.on("connected", () => {
        client.makeCall(this.number);
      });

      await new Promise<void>((resolve, reject) => {
        client.on("call:terminated", (id: string) => {
          if (id === callId) {
            window.parent.postMessage("end", "*");
            client.destroy();
            resolve();
          }
        });

        client.on("call:make:error", () => {
          client.destroy();
          reject(new Error("Falha ao iniciar a chamada"));
        });
      });

      console.log("Chamada one-shot finalizada. callId:", callId);
      this.reset();
    } catch (err) {
      this.status = "error";
      this.errorMessage =
        err instanceof Error
          ? err.message
          : "Erro desconhecido ao iniciar chamada";
      console.error("Erro na chamada one-shot:", err);
    }
  }
}
